<template>
  <chatflow-page>
    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="tableItems"
      class="shadow-xl cf-card"
      elevation="2"
      :search="search"
      @click:row="openSession"
    >
      <template v-slot:top>
        <v-toolbar flat color="white" class="px-2">
          <v-toolbar-title class="text-h6 font-weight-medium">Konuşmalar</v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="search"
            placeholder="Ara…"
            class="cf-conv-search mr-3"
            style="max-width: 220px"
          />
          <v-btn text small color="primary" :loading="loading" @click="load">
            <v-icon left small>refresh</v-icon>
            Yenile
          </v-btn>
        </v-toolbar>
        <v-divider />
      </template>

      <template v-slot:item.contact="{ item }">
        <span class="font-weight-medium">{{ contactName(item) }}</span>
      </template>

      <template v-slot:item.channel="{ item }">
        <channel-pill v-if="item.channel" :type="item.channel.type" :name="item.channel.name" />
        <span v-else class="grey--text">—</span>
      </template>

      <template v-slot:item.flow="{ item }">
        {{ item.flow ? item.flow.name : "—" }}
      </template>

      <template v-slot:item.agentMode="{ item }">
        <v-chip v-if="item.agentMode" small color="warning" outlined>
          {{ item.variables?.agentOperatorName || "Operatör" }}
        </v-chip>
        <span v-else class="grey--text text--darken-1">Bot</span>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" dark>
          {{ statusLabelTr(item.status) }}
        </v-chip>
      </template>

      <template v-slot:item.updatedAt="{ item }">
        {{ formatWhen(item.updatedAt) }}
      </template>
    </v-data-table>

    <v-dialog v-model="detailDialog" max-width="720" scrollable content-class="cf-conv-dialog">
      <v-card v-if="detail" class="cf-conv-card">
        <v-card-title class="cf-conv-head">
          <div class="cf-conv-head__main">
            <div class="text-h6 font-weight-medium">{{ userLabel }}</div>
            <div class="cf-conv-head__meta">
              <channel-pill
                v-if="detail.session.channel"
                :type="detail.session.channel.type"
                :name="channelLabel"
              />
              <span v-if="flowLabel" class="cf-dot">·</span>
              <span v-if="flowLabel">{{ flowLabel }}</span>
            </div>
          </div>
          <v-btn icon small @click="detailDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <div class="cf-conv-toolbar px-4 pb-3">
          <v-chip small :color="statusColor(detail.session.status)" dark class="mr-2">
            {{ statusLabelTr(detail.session.status) }}
          </v-chip>
          <v-chip v-if="agentMode" small color="warning" class="mr-2">
            <v-icon left x-small>support_agent</v-icon>
            {{ agentDisplayName }} devrede
          </v-chip>
          <v-spacer />
          <v-switch
            v-model="agentMode"
            :loading="agentToggling"
            dense
            hide-details
            class="mt-0 cf-agent-switch"
            label="Botu durdur"
            @change="onAgentModeChange"
          />
        </div>

        <v-divider />

        <v-card-text class="cf-conv-messages pa-0">
          <div ref="messagePane" class="cf-conv-messages__scroll pa-4" @scroll="onMessageScroll">
            <div v-if="hasMoreOlder" class="cf-load-older-wrap">
              <v-btn
                small
                text
                color="primary"
                :loading="loadingOlder"
                :disabled="loadingOlder"
                @click="loadOlderMessages"
              >
                Daha eski mesajları yükle
              </v-btn>
            </div>
            <div v-if="!detail.messages.length" class="cf-conv-empty">
              Henüz mesaj yok. Kullanıcı yazdığında burada görünür.
            </div>
            <div
              v-for="msg in detail.messages"
              :key="msg.id"
              class="cf-bubble-row"
              :class="'cf-bubble-row--' + bubbleSide(msg)"
            >
              <div class="cf-bubble" :class="'cf-bubble--' + bubbleKind(msg)">
                <div class="cf-bubble__label">{{ bubbleLabel(msg) }}</div>
                <div class="cf-bubble__text">{{ msg.text || "—" }}</div>
                <div class="cf-bubble__time">{{ formatWhen(msg.createdAt) }}</div>
              </div>
            </div>
            <div ref="messagesEnd" class="cf-messages-anchor" aria-hidden="true" />
          </div>
        </v-card-text>

        <v-divider />

        <div class="cf-conv-compose px-4 py-3">
          <v-alert v-if="!agentMode" dense text type="info" class="mb-3 py-2">
            Kendiniz yazmak için üstte <strong>Botu durdur</strong> anahtarını açın.
          </v-alert>
          <v-select
            v-model="sendType"
            :items="sendTypeItems"
            :disabled="!agentMode || sending"
            label="Gönderim türü"
            dense
            outlined
            hide-details
            class="mb-3"
          />
          <template v-if="sendType === 'text'">
            <v-textarea
              v-model="replyText"
              :disabled="!agentMode || sending"
              auto-grow
              rows="2"
              outlined
              dense
              hide-details
              label="Metin"
              name="chatflow-reply-text"
              autocomplete="off"
              placeholder="Mesajınızı yazın…"
              @keydown.enter.exact.prevent="sendReply"
            />
          </template>
          <template v-else-if="sendType === 'photo' || sendType === 'document'">
            <v-text-field
              v-model="mediaUrl"
              :disabled="!agentMode || sending"
              label="URL veya Telegram file_id"
              placeholder="https://…"
              dense
              outlined
              hide-details
              class="mb-2"
            />
            <v-text-field
              v-model="mediaCaption"
              :disabled="!agentMode || sending"
              label="Açıklama (isteğe bağlı)"
              dense
              outlined
              hide-details
            />
          </template>
          <template v-else-if="sendType === 'buttons' || sendType === 'list'">
            <v-textarea
              v-model="choiceText"
              :disabled="!agentMode || sending"
              rows="2"
              outlined
              dense
              hide-details
              label="Mesaj metni"
              class="mb-2"
            />
            <v-select
              v-model="choiceStyle"
              :items="choiceStyleItems"
              :disabled="!agentMode || sending"
              label="Tuş tipi"
              dense
              outlined
              hide-details
              class="mb-2"
            />
            <div class="cf-choice-head">
              <span class="caption font-weight-medium">Seçenekler</span>
              <v-btn
                x-small
                text
                color="primary"
                :disabled="!agentMode || sending"
                @click="addChoice"
              >
                + Ekle
              </v-btn>
            </div>
            <div v-for="(ch, idx) in choices" :key="idx" class="cf-choice-row">
              <v-text-field
                v-model="ch.label"
                :disabled="!agentMode || sending"
                label="Tuş metni"
                dense
                outlined
                hide-details
                class="cf-choice-field"
              />
              <v-text-field
                v-model="ch.value"
                :disabled="!agentMode || sending"
                label="Değer"
                dense
                outlined
                hide-details
                class="cf-choice-field cf-choice-field--sm"
              />
              <v-text-field
                v-if="sendType === 'list'"
                v-model="ch.description"
                :disabled="!agentMode || sending"
                label="Açıklama"
                dense
                outlined
                hide-details
                class="cf-choice-field"
              />
              <v-btn
                icon
                x-small
                :disabled="!agentMode || sending || choices.length <= 1"
                @click="removeChoice(idx)"
              >
                <v-icon small>close</v-icon>
              </v-btn>
            </div>
          </template>
          <div class="cf-conv-compose__row mt-3">
            <v-spacer />
            <v-btn color="primary" depressed :disabled="!canSend" :loading="sending" @click="sendReply">
              <v-icon left small>send</v-icon>
              Gönder
            </v-btn>
          </div>
        </div>

        <v-divider />

        <v-card-actions class="px-4 py-3">
          <v-btn text small color="error" :loading="resetting" @click="resetSession">
            Oturumu sıfırla
          </v-btn>
          <v-spacer />
          <v-btn text @click="detailDialog = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack" :color="snackColor" bottom :timeout="3500">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn text dark v-bind="attrs" @click="snack = false">Kapat</v-btn>
      </template>
    </v-snackbar>
  </chatflow-page>
</template>

<script>
import { mapState } from "vuex";
import ChatflowApi from "../api";
import ChatflowAuthMixin from "../mixins/auth";
import ChatflowPage from "../components/ChatflowPage.vue";
import ChannelPill from "../components/ChannelPill.vue";
import { sessionContactName, statusLabelTr } from "../utils/channelBranding";

export default {
  components: { ChatflowPage, ChannelPill },
  mixins: [ChatflowAuthMixin("Chatflow Conversations")],
  data: () => ({
    loading: false,
    sessions: [],
    search: "",
    detailDialog: false,
    detail: null,
    agentMode: false,
    agentToggling: false,
    sendType: "text",
    sendTypeItems: [
      { value: "text", text: "Metin" },
      { value: "buttons", text: "Butonlar" },
      { value: "list", text: "Liste" },
      { value: "photo", text: "Fotoğraf" },
      { value: "document", text: "Dosya" },
    ],
    replyText: "",
    mediaUrl: "",
    mediaCaption: "",
    choiceText: "",
    choiceStyle: "reply",
    choiceStyleItems: [
      { value: "reply", text: "Klavye tuşları" },
      { value: "inline", text: "Satır içi (inline)" },
    ],
    choices: [{ label: "", value: "", description: "" }],
    pollTimer: null,
    pollInFlight: false,
    pollIntervalMs: 3000,
    hasMoreOlder: false,
    loadingOlder: false,
    messagePageSize: 50,
    stickToBottom: true,
    sending: false,
    resetting: false,
    snack: false,
    snackText: "",
    snackColor: "success",
    headers: [
      { text: "Kullanıcı", value: "contact", sortable: false },
      { text: "Kanal", value: "channel", sortable: false },
      { text: "Akış", value: "flow", sortable: false },
      { text: "Mod", value: "agentMode", sortable: false },
      { text: "Durum", value: "status" },
      { text: "Güncellendi", value: "updatedAt" },
    ],
  }),
  computed: {
    tableItems() {
      return this.sessions;
    },
    ...mapState("user", {
      currentUser: (state) => state.user || {},
    }),
    currentOperatorName() {
      return this.currentUser?.Name || this.currentUser?.name || "";
    },
    agentDisplayName() {
      return (
        this.detail?.session?.variables?.agentOperatorName ||
        this.currentOperatorName ||
        "Operatör"
      );
    },
    channelLabel() {
      return this.detail?.session?.channel?.name || "Kanal";
    },
    userLabel() {
      const s = this.detail?.session;
      return s ? sessionContactName(s) : "Kullanıcı";
    },
    flowLabel() {
      return this.detail?.session?.flow?.name || "";
    },
    canSend() {
      if (!this.agentMode || this.sending) return false;
      if (this.sendType === "text") return Boolean(this.replyText.trim());
      if (this.sendType === "photo" || this.sendType === "document") {
        return Boolean(this.mediaUrl.trim());
      }
      if (this.sendType === "buttons" || this.sendType === "list") {
        const filled = this.choices.some((c) => String(c.label || c.value || "").trim());
        return filled;
      }
      return false;
    },
  },
  watch: {
    detailDialog(open) {
      if (open) {
        this.startPolling();
        this.$nextTick(() => {
          setTimeout(() => this.scrollMessagesToEnd(), 120);
        });
      } else {
        this.stopPolling();
      }
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    contactName(session) {
      return sessionContactName(session);
    },
    statusLabelTr,
    notify(text, color = "success") {
      this.snackText = text;
      this.snackColor = color;
      this.snack = true;
    },
    statusColor(status) {
      if (status === "active") return "success";
      if (status === "completed" || status === "ended") return "grey";
      if (status === "error") return "error";
      return "primary";
    },
    formatWhen(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleString("tr-TR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return String(value);
      }
    },
    parsePayload(msg) {
      const raw = msg?.payload;
      if (!raw) return null;
      if (typeof raw === "object") return raw;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    },
    bubbleSide(msg) {
      return msg.direction === "in" ? "in" : "out";
    },
    bubbleKind(msg) {
      if (msg.direction === "in") return "in";
      const p = this.parsePayload(msg);
      if (p?.sender === "agent") return "agent";
      return "bot";
    },
    bubbleLabel(msg) {
      if (msg.direction === "in") return "Gelen";
      if (this.bubbleKind(msg) !== "agent") return "Bot";
      const p = this.parsePayload(msg);
      return (
        p?.operatorName ||
        p?.reply?.operatorName ||
        this.currentOperatorName ||
        "Operatör"
      );
    },
    resetComposeForm() {
      this.sendType = "text";
      this.replyText = "";
      this.mediaUrl = "";
      this.mediaCaption = "";
      this.choiceText = "";
      this.choiceStyle = "reply";
      this.choices = [{ label: "", value: "", description: "" }];
    },
    addChoice() {
      this.choices.push({ label: "", value: "", description: "" });
    },
    removeChoice(idx) {
      if (this.choices.length <= 1) return;
      this.choices.splice(idx, 1);
    },
    buildOutgoingMessage() {
      if (this.sendType === "text") {
        return { type: "text", text: this.replyText.trim() };
      }
      if (this.sendType === "photo") {
        return {
          type: "photo",
          url: this.mediaUrl.trim(),
          caption: this.mediaCaption.trim(),
        };
      }
      if (this.sendType === "document") {
        return {
          type: "document",
          url: this.mediaUrl.trim(),
          caption: this.mediaCaption.trim(),
        };
      }
      const choices = this.choices
        .map((c) => ({
          label: String(c.label || "").trim(),
          value: String(c.value || c.label || "").trim(),
          description: String(c.description || "").trim(),
        }))
        .filter((c) => c.label || c.value);
      return {
        type: this.sendType,
        text: this.choiceText.trim(),
        buttonStyle: this.choiceStyle,
        choices,
      };
    },
    syncAgentModeFromDetail() {
      this.agentMode = Boolean(
        this.detail?.agentMode ?? this.detail?.session?.variables?.agentMode
      );
    },
    isNearBottom() {
      const pane = this.$refs.messagePane;
      if (!pane) return true;
      return pane.scrollHeight - pane.scrollTop - pane.clientHeight < 80;
    },
    onMessageScroll() {
      this.stickToBottom = this.isNearBottom();
    },
    scrollMessagesToEnd() {
      const scroll = () => {
        const pane = this.$refs.messagePane;
        if (pane) pane.scrollTop = pane.scrollHeight;
      };
      this.$nextTick(() => {
        scroll();
        requestAnimationFrame(scroll);
        setTimeout(scroll, 80);
      });
    },
    mergeMessageMeta(data) {
      if (data.hasMoreOlder != null) this.hasMoreOlder = Boolean(data.hasMoreOlder);
    },
    applyDetail(data, { forceScroll = false, prepend = false } = {}) {
      const incoming = data.messages || [];
      let merged = incoming;

      if (prepend && this.detail?.messages?.length) {
        const seen = new Set(this.detail.messages.map((m) => m.id));
        const older = incoming.filter((m) => !seen.has(m.id));
        merged = [...older, ...this.detail.messages];
      } else if (!prepend && this.detail?.messages?.length && !forceScroll) {
        const prev = this.detail.messages;
        const lastPrev = prev[prev.length - 1]?.id;
        const lastIn = incoming[incoming.length - 1]?.id;
        if (lastPrev === lastIn && incoming.length >= prev.length) {
          merged = incoming;
        }
      }

      const prevMessages = this.detail?.messages || [];
      const hasNewAtEnd =
        merged.length > prevMessages.length ||
        (merged.length > 0 &&
          merged[merged.length - 1]?.id !== prevMessages[prevMessages.length - 1]?.id);

      this.detail = { ...data, messages: merged };
      this.mergeMessageMeta(data);
      this.syncAgentModeFromDetail();

      if (forceScroll || (hasNewAtEnd && this.stickToBottom)) {
        this.scrollMessagesToEnd();
      }
    },
    async loadOlderMessages() {
      if (!this.detail?.session?.id || !this.hasMoreOlder || this.loadingOlder) return;
      const oldestId = this.detail.messages[0]?.id;
      if (!oldestId) return;

      const pane = this.$refs.messagePane;
      const prevHeight = pane ? pane.scrollHeight : 0;
      const prevTop = pane ? pane.scrollTop : 0;

      this.loadingOlder = true;
      try {
        const data = await ChatflowApi.getConversation(this.detail.session.id, {
          beforeId: oldestId,
          limit: this.messagePageSize,
        });
        const seen = new Set(this.detail.messages.map((m) => m.id));
        const older = (data.messages || []).filter((m) => !seen.has(m.id));
        if (older.length) {
          this.detail.messages = [...older, ...this.detail.messages];
        }
        this.mergeMessageMeta(data);
        this.stickToBottom = false;
        this.$nextTick(() => {
          if (pane) {
            pane.scrollTop = pane.scrollHeight - prevHeight + prevTop;
          }
        });
      } catch (e) {
        this.notify(e.message || "Eski mesajlar yüklenemedi", "error");
      }
      this.loadingOlder = false;
    },
    patchSessionInList(session, agentMode) {
      if (!session?.id) return;
      const idx = this.sessions.findIndex((s) => s.id === session.id);
      if (idx < 0) return;
      this.$set(this.sessions, idx, {
        ...this.sessions[idx],
        ...session,
        agentMode: agentMode ?? this.sessions[idx].agentMode,
      });
    },
    startPolling() {
      this.stopPolling();
      this.pollConversation();
      this.pollTimer = setInterval(() => {
        this.pollConversation();
      }, this.pollIntervalMs);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async pollConversation() {
      if (
        !this.detailDialog ||
        !this.detail?.session?.id ||
        this.pollInFlight ||
        this.sending ||
        this.agentToggling ||
        this.resetting ||
        (typeof document !== "undefined" && document.visibilityState === "hidden")
      ) {
        return;
      }
      this.pollInFlight = true;
      try {
        const prevUpdated = this.detail.session?.updatedAt;
        const lastId = this.detail.messages[this.detail.messages.length - 1]?.id;
        const data = lastId
          ? await ChatflowApi.getConversation(this.detail.session.id, { afterId: lastId })
          : await ChatflowApi.getConversation(this.detail.session.id, {
              limit: this.messagePageSize,
            });

        if (data.session) {
          this.detail.session = data.session;
          this.syncAgentModeFromDetail();
        }

        if (lastId && data.messages?.length) {
          const seen = new Set(this.detail.messages.map((m) => m.id));
          const added = data.messages.filter((m) => !seen.has(m.id));
          if (added.length) {
            this.$set(this.detail, "messages", [...this.detail.messages, ...added]);
            if (this.stickToBottom) this.scrollMessagesToEnd();
          }
        } else if (!lastId) {
          this.applyDetail(data);
        } else if (
          data.session?.updatedAt &&
          String(data.session.updatedAt) !== String(prevUpdated)
        ) {
          await this.refreshDetail();
        }

        this.patchSessionInList(data.session || this.detail.session, data.agentMode);
      } catch {
        /* sessiz */
      }
      this.pollInFlight = false;
    },
    async load() {
      this.loading = true;
      try {
        this.sessions = await ChatflowApi.getConversations();
      } catch (e) {
        this.notify(e.message || "Konuşmalar yüklenemedi", "error");
      }
      this.loading = false;
    },
    async openSession(session) {
      try {
        this.resetComposeForm();
        this.hasMoreOlder = false;
        this.stickToBottom = true;
        const data = await ChatflowApi.getConversation(session.id, {
          limit: this.messagePageSize,
        });
        this.applyDetail(data, { forceScroll: true });
        this.detailDialog = true;
      } catch (e) {
        this.notify(e.message || "Konuşma açılamadı", "error");
      }
    },
    async refreshDetail() {
      if (!this.detail?.session?.id) return;
      const data = await ChatflowApi.getConversation(this.detail.session.id, {
        limit: Math.max(this.detail.messages.length, this.messagePageSize),
      });
      this.applyDetail(data, { forceScroll: true });
      this.patchSessionInList(data.session, data.agentMode);
    },
    async onAgentModeChange(enabled) {
      if (!this.detail?.session?.id) return;
      this.agentToggling = true;
      try {
        const data = await ChatflowApi.setConversationAgentMode(
          this.detail.session.id,
          enabled,
          this.currentOperatorName
        );
        this.applyDetail({
          ...this.detail,
          session: data.session,
          agentMode: data.agentMode,
        });
        this.notify(enabled ? "Bot durduruldu — yazabilirsiniz" : "Bot tekrar aktif");
        await this.load();
      } catch (e) {
        this.syncAgentModeFromDetail();
        this.notify(e.message || "Mod değiştirilemedi", "error");
      }
      this.agentToggling = false;
    },
    async sendReply() {
      if (!this.canSend || !this.detail?.session?.id) return;
      this.sending = true;
      try {
        const lastId = this.detail.messages[this.detail.messages.length - 1]?.id;
        const data = await ChatflowApi.sendConversationMessage(
          this.detail.session.id,
          this.buildOutgoingMessage(),
          this.currentOperatorName,
          lastId
        );
        this.resetComposeForm();
        const seen = new Set(this.detail.messages.map((m) => m.id));
        const added = (data.messages || []).filter((m) => !seen.has(m.id));
        if (added.length) {
          this.detail.messages = [...this.detail.messages, ...added];
        }
        if (data.session) this.detail.session = data.session;
        if (data.agentMode != null) this.detail.agentMode = data.agentMode;
        this.syncAgentModeFromDetail();
        this.stickToBottom = true;
        this.scrollMessagesToEnd();
        this.patchSessionInList(data.session, data.agentMode);
      } catch (e) {
        this.notify(e.message || "Mesaj gönderilemedi", "error");
      }
      this.sending = false;
    },
    async resetSession() {
      if (!this.detail?.session?.id) return;
      if (!window.confirm("Oturum sıfırlansın mı? Akış baştan başlar.")) return;
      this.resetting = true;
      try {
        await ChatflowApi.resetConversation(this.detail.session.id);
        await this.refreshDetail();
        this.agentMode = false;
        await this.load();
        this.notify("Oturum sıfırlandı");
      } catch (e) {
        this.notify(e.message || "Sıfırlanamadı", "error");
      }
      this.resetting = false;
    },
  },
};
</script>

<style scoped>
.cf-card {
  border-radius: 8px;
}

.cf-conv-card {
  border-radius: 12px;
  overflow: hidden;
}

.cf-conv-head {
  display: flex;
  align-items: flex-start;
  padding: 16px 16px 8px;
}

.cf-conv-head__meta {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.cf-dot {
  margin: 0 6px;
}

.cf-conv-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.cf-agent-switch >>> .v-label {
  font-size: 13px;
  font-weight: 500;
}

.cf-conv-messages {
  background: #f4f2f5;
  overflow: hidden;
}

.cf-conv-messages__scroll {
  display: flex;
  flex-direction: column;
  max-height: 420px;
  min-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
}

.cf-load-older-wrap {
  display: flex;
  justify-content: center;
  padding-bottom: 12px;
  flex-shrink: 0;
}

.cf-messages-anchor {
  height: 1px;
  flex-shrink: 0;
}

.cf-conv-empty {
  text-align: center;
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
  padding: 48px 16px;
}

.cf-bubble-row {
  display: flex;
  margin-bottom: 12px;
}

.cf-bubble-row--in {
  justify-content: flex-start;
}

.cf-bubble-row--out {
  justify-content: flex-end;
}

.cf-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.cf-bubble--in {
  background: #fff;
  border-bottom-left-radius: 4px;
}

.cf-bubble--bot {
  background: #663f5c;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.cf-bubble--agent {
  background: #4f3147;
  color: #fff;
  border-bottom-right-radius: 4px;
  outline: 2px solid rgba(255, 255, 255, 0.35);
}

.cf-bubble__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;
  margin-bottom: 4px;
}

.cf-bubble--in .cf-bubble__label {
  color: rgba(0, 0, 0, 0.45);
}

.cf-bubble__text {
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.cf-bubble__time {
  font-size: 11px;
  margin-top: 6px;
  opacity: 0.75;
  text-align: right;
}

.cf-conv-compose__row {
  display: flex;
  align-items: center;
}

.cf-choice-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.cf-choice-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.cf-choice-field {
  flex: 1 1 140px;
  min-width: 120px;
}

.cf-choice-field--sm {
  flex: 0 1 100px;
  max-width: 120px;
}
</style>
